/** The pure part of a map's relationship lines: which relationship rows it draws, and where their ends go. */

export interface RelationshipEdge {
  /** The row's relationshipId: a line click reports it together with the source twin. */
  id: bigint;
  source: string;
  target: string;
  name: string;
  /** Relative to the shown twin, as the 3D viewer colours it; a row shown by its type is outgoing. */
  direction: 'outgoing' | 'incoming';
}

const NONE: ReadonlySet<string> = new Set();

/** Rows touching a shown twin, both directions (same rule as the 3D viewer), plus every
 *  row whose relationship name is in `shownTypes` (the type filter). A row's
 *  relationship name is its `name` column. */
export function relationshipEdges(
  rows: Iterable<{
    relationshipId: bigint;
    sourceTwinId: string;
    targetTwinId: string;
    name: string;
  }>,
  shownTwinIds: ReadonlySet<string>,
  shownTypes: ReadonlySet<string> = NONE,
): RelationshipEdge[] {
  const out: RelationshipEdge[] = [];
  for (const { relationshipId: id, sourceTwinId: source, targetTwinId: target, name } of rows) {
    if (shownTwinIds.has(source) || (!shownTwinIds.has(target) && shownTypes.has(name)))
      out.push({ id, source, target, name, direction: 'outgoing' });
    else if (shownTwinIds.has(target))
      out.push({ id, source, target, name, direction: 'incoming' });
  }
  return out;
}

export interface LatLngLike {
  lat: number;
  lng: number;
}

/** Map event a host editor fires with the live position of the twin it edits (`at`, null when it
 *  closes): that twin is out of the cluster group while edited, so no marker answers for it. */
export const LIVE_END_EVENT = 'tt:twin-live-position';
export interface LiveEnd {
  twinId: string;
  at: LatLngLike;
}

/** Where a line end goes: the twin being edited at its live position, else `placed` (its marker,
 *  cluster bubble or geo centre). Null when that is not a finite point: the edge is skipped,
 *  never drawn through NaN (Leaflet throws "Invalid LatLng object"). */
export function lineEnd(
  twinId: string,
  live: LiveEnd | null,
  placed: (twinId: string) => LatLngLike | null | undefined,
): LatLngLike | null {
  const p = live?.twinId === twinId ? live.at : placed(twinId);
  return p && Number.isFinite(p.lat) && Number.isFinite(p.lng) ? p : null;
}

export interface EdgeBundle {
  a: LatLngLike;
  b: LatLngLike;
  /** The first edge of the bundle (its name and direction are the bundle's). */
  edge: RelationshipEdge;
  /** Every edge drawn as this line, `edge` first. */
  edges: RelationshipEdge[];
}

/** The lines to draw: edges with both ends placed (`lineEnd`) and apart, those
 *  whose ends collapse onto the same bubble pair (same name and direction) once. */
export function bundleEdges(
  edges: readonly RelationshipEdge[],
  live: LiveEnd | null,
  placed: (twinId: string) => LatLngLike | null | undefined,
): EdgeBundle[] {
  const bundles = new Map<string, EdgeBundle>();
  for (const edge of edges) {
    const a = lineEnd(edge.source, live, placed);
    const b = lineEnd(edge.target, live, placed);
    if (!a || !b || (a.lat === b.lat && a.lng === b.lng)) continue;
    const key = `${a.lat},${a.lng}|${b.lat},${b.lng}|${edge.name}|${edge.direction}`;
    const bundle = bundles.get(key);
    if (bundle) bundle.edges.push(edge);
    else bundles.set(key, { a, b, edge, edges: [edge] });
  }
  return [...bundles.values()];
}
